import { useCallback, useMemo, useRef, useState } from "react";

// Shape Web Defender has no spoken lines — like the iOS ShapeWeb game it uses
// only procedural WebAudio chimes (a rising "pop" on a match, a low "thud" on
// a miss, brighter arpeggios for combos / golden catches, and a descending
// tone for the breach). Everything is a lightweight oscillator so the effects
// layer works offline and never blocks play.

const STORAGE_KEY = "pixley_shape_web_muted";

const createAudioContext = () => {
  if (typeof window === "undefined") return null;
  const AudioContext = window.AudioContext || window.webkitAudioContext;
  return AudioContext ? new AudioContext() : null;
};

// notes: [{ freq, start, dur }]
const CHIMES = {
  tap: { type: "sine", peak: 0.05, notes: [{ freq: 420, start: 0, dur: 0.12 }] },
  correct: {
    type: "sine",
    peak: 0.06,
    notes: [
      { freq: 523, start: 0, dur: 0.13 },
      { freq: 659, start: 0.08, dur: 0.14 },
      { freq: 784, start: 0.16, dur: 0.18 },
    ],
  },
  wrong: {
    type: "triangle",
    peak: 0.05,
    notes: [
      { freq: 220, start: 0, dur: 0.16 },
      { freq: 165, start: 0.09, dur: 0.2 },
    ],
  },
  blast: {
    type: "triangle",
    peak: 0.07,
    notes: [
      { freq: 660, start: 0, dur: 0.12 },
      { freq: 880, start: 0.09, dur: 0.14 },
      { freq: 1175, start: 0.19, dur: 0.22 },
    ],
  },
  victory: {
    type: "triangle",
    peak: 0.07,
    notes: [
      { freq: 523, start: 0, dur: 0.16 },
      { freq: 659, start: 0.12, dur: 0.16 },
      { freq: 784, start: 0.24, dur: 0.18 },
      { freq: 1047, start: 0.36, dur: 0.26 },
    ],
  },
  breach: {
    type: "sawtooth",
    peak: 0.08,
    notes: [
      { freq: 320, start: 0, dur: 0.18 },
      { freq: 220, start: 0.14, dur: 0.2 },
      { freq: 130, start: 0.3, dur: 0.3 },
    ],
  },
};

export const useShapeWebSound = () => {
  const [muted, setMuted] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });
  const mutedRef = useRef(muted);
  mutedRef.current = muted;
  const contextRef = useRef(null);

  const ensureAudio = useCallback(async () => {
    if (!contextRef.current) contextRef.current = createAudioContext();
    if (contextRef.current?.state === "suspended") {
      try {
        await contextRef.current.resume();
      } catch {
        // Resuming can reject with no gesture yet — safe to ignore.
      }
    }
    return contextRef.current;
  }, []);

  const play = useCallback(
    async (kind) => {
      if (mutedRef.current) return;
      const chime = CHIMES[kind];
      if (!chime) return;
      const context = await ensureAudio();
      if (!context) return;
      const now = context.currentTime;
      chime.notes.forEach(({ freq, start, dur }) => {
        const oscillator = context.createOscillator();
        const gain = context.createGain();
        const startsAt = now + start;
        oscillator.type = chime.type;
        oscillator.frequency.setValueAtTime(freq, startsAt);
        gain.gain.setValueAtTime(0.0001, startsAt);
        gain.gain.exponentialRampToValueAtTime(chime.peak, startsAt + 0.015);
        gain.gain.exponentialRampToValueAtTime(0.0001, startsAt + dur);
        oscillator.connect(gain);
        gain.connect(context.destination);
        oscillator.start(startsAt);
        oscillator.stop(startsAt + dur + 0.03);
      });
    },
    [ensureAudio]
  );

  const stop = useCallback(() => {
    // Oscillators are one-shot and self-stopping; nothing to tear down.
  }, []);

  const toggleMute = useCallback(() => {
    setMuted((current) => {
      const next = !current;
      try {
        localStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        // The game still works when storage is unavailable.
      }
      return next;
    });
  }, []);

  return useMemo(() => ({ muted, toggleMute, play, stop }), [muted, toggleMute, play, stop]);
};
